<template>
	<v-container
		grid-list-xl
	>
		<v-layout
			row
			justify-center
		>
			<v-flex
				xs12
				md8
				pa-0
			>
				<slot name="alert"/>
			</v-flex>
		</v-layout>
		<OutputNew v-if="$userCan('manage_outputs') && !project.isArchived" />
		<v-layout
			v-if="$userCan('manage_outputs')"
			row
			justify-center
		>
			<v-btn
				color="primary"
				class="mb-3"
				ripple
				:disabled="allOutputsLoading"
				:loading="allOutputsLoading"
				@click="$refs.importOutput.show = true"
			>
				Import Outputs
			</v-btn>
		</v-layout>
		<ImportOutput
			v-if="$userCan('manage_outputs') && !allOutputsLoading"
			ref="importOutput"
			@importing="importing = $event"
		/>
		<ExportOutput
			v-if="$userCan('manage_outputs')"
			ref="exportOutput"
		/>
		<OutputIterator
			:items="outputs"
			:loading="importing"
			@export="exportOutput"
			@toggle-shared="toggleShared"
			@set-default="setDefaultOutput"
			@show="setAndShowOutput"
			@update="setUpdateOutput"
			@delete="deleteOutput"
		/>
		<FullDialog
			:dialog="showOutput"
			dialog-title="Output Report"
			@close-dialog="showOutput = false"
		>
			<template slot="dialog-content">
				<OutputView
					ref="outputView"
					:outputs="outputs"
					:initial-output="currentOutput"
					:reports="reports"
				/>
			</template>
		</FullDialog>
		<v-dialog
			v-if="$userCan('manage_outputs')"
			v-model="editOutput.show"
			ref="dialog"
			max-width="800px"
			@input="resetEditOutput"
		>
			<v-card v-if="editOutput.show">
				<OutputEdit
					:edit-output="editOutput.output"
					:existing-names="outputNamesMinusCurrent(editOutput.output.name)"
					@close="closeEditOutput"
				/>
			</v-card>
		</v-dialog>
		<Confirm ref="confirm" />
	</v-container>
</template>

<script>
import OutputIterator from '@/components/Output/OutputIterator';
import FullDialog from '@/components/FullDialog';
import OutputView from '@/components/Report/OutputView';
import OutputNew from '@/components/Report/OutputNew';
import OutputEdit from '@/components/Report/OutputEdit';
import Confirm from '@/components/Confirm';

export default {
	components: {
		OutputIterator,
		FullDialog,
		OutputView,
		OutputNew,
		OutputEdit,
		Confirm,
		ImportOutput: () => import('@/components/Output/ImportOutput'),
		ExportOutput: () => import('@/components/Output/ExportOutput'),
	},
	data() {
		return {
			currentOutput: {},
			showOutput: false,
			editOutput: { show: false, output: null },
			allOutputsLoading: true,
			importing: false,
		};
	},
	created() {
		if (this.project.userCan('manage_outputs')) {
			this.$store.dispatch('outputs/loadAllOutputsByProject').then((resp) => {
				this.allOutputsLoading = false;
			}).catch((e) => {
				this.$notify('error', 'Failed to fetch outputs.');
			});
		}
	},
	computed: {
		project() {
			return this.$store.get('projects/currentProject');
		},
		outputs() {
			return this.project.sortedOutputs;
		},
		outputNames() {
			return this.outputs.map(output => output.name);
		},
		reports() {
			return this.$store.get('reports/active').models;
		},
	},
	watch: {
		// Force refresh in rare case somebody views output, exits and updates
		// from iterator, clicks same output #466
		showOutput(val) {
			if (val) {
				this.$refs.outputView.refreshFrame();
			}
		},
	},
	methods: {
		outputNamesMinusCurrent(name) {
			const names = this.outputNames.filter(o => o !== name);
			return names;
		},
		setDefaultOutput(output) {
			if (output.default)
				return;

			var outputsToUpdate = this.outputs.filter(item => item.id !== output.id && item.default);

			output.default = true;
			outputsToUpdate.forEach(item => item.default = false);

			output.save().then(() => {
				this.$notify('success', `Output "${output.name}" has been set as default.`);
				outputsToUpdate.forEach(item => item.save()); // Save other outputs only on successfull save on this output
			}).catch(() => {
				this.$notify('error', 'Failed to update output');
			});
		},
		exportOutput(output) {
			this.$refs.exportOutput.output = output;
			this.$refs.exportOutput.show = true;
		},
		toggleShared(output) {
			output.shared = !output.shared;
			const sharedText = output.shared ? 'set' : 'unset';
			output.save().then(() => {
				this.$notify('success', `Output "${output.name}" has been ${sharedText} as shared.`);
			}).catch((e) => {
				this.$notify('error', 'Failed to update output');
			});
		},
		setAndShowOutput(output) {
			this.currentOutput = output;
			this.showOutput = !this.showOutput;
		},
		setUpdateOutput(output) {
			this.editOutput = { show: true, output };
		},
		updateOutput(output) {
			output.save().then(() => {
				this.$notify('success', `Output ${output.name} has been updated.`);
			}).catch(() => {
				this.$notify('error', 'Failed to update output');
			}).finally(() => {
				this.editOutput.show = false;
			});
		},
		deleteOutput(output) {
			const name = output.name;

			this.$refs.confirm.createConfirm(`This will delete "${name}".`, 'error').then((answer) => {
				if (!answer) {
					return;
				}
				output.delete().then(() => {
					this.outputs.forEach((o) => {
						if (!o.id) {
							const deleteIndex = this.outputs.indexOf(o);
							this.outputs.splice(deleteIndex, 1);
						}
					});
					this.$notify('success', `Output "${name}" has been deleted.`);
				}).catch(() => {
					this.$notify('error', `Failed to delete output "${name}".`);
				});
			});
		},
		resetEditOutput(val) {
			if (!val) {
				this.editOutput.output.reset();
			}
		},
		closeEditOutput() {
			this.editOutput.show = false;
			this.editOutput.output.reset();
		},
	},
};
</script>
